// LRPO-5797 — autosave the territory name in the rename modal.
//
// The territory name is NOT part of the builder's #exhibitor-form. It lives in its
// own form (_territory_name_modal.html.erb) posting to PATCH update_name. Because
// every edit is now saved automatically, the modal discard button is relabelled
// "Close" — a button reading "Cancel" would be a lie once the rename is committed.
// The one-request-at-a-time guard here prevents two saves racing (the lesson from
// LRPO-5804).

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { delay: { type: Number, default: 3000 } }

  connect() {
    this.form = this.element.tagName === "FORM" ? this.element : this.element.querySelector("form")
    if (!this.form) return

    this.input = this.form.querySelector('input[name="name"]')
    if (!this.input) return

    this.lastSaved = this.input.value
    this.timer = null
    this.saving = false
    this.dirtyWhileSaving = false

    this._schedule = this.schedule.bind(this)
    this._cancelPending = this.cancelPending.bind(this)

    this.form.addEventListener("input", this._schedule)
    this.form.addEventListener("submit", this._cancelPending)
  }

  disconnect() {
    this.cancelPending()
    if (!this.form || !this._schedule) return
    this.form.removeEventListener("input", this._schedule)
    this.form.removeEventListener("submit", this._cancelPending)
  }

  schedule() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), this.delayValue)
  }

  cancelPending() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  save() {
    const value = this.input.value

    if (value.trim() === "") return
    if (value.length > 250) return
    if (value === this.lastSaved) return
    if (this.saving) {
      this.dirtyWhileSaving = true
      return
    }

    this.saving = true
    const previous = this.lastSaved
    this.lastSaved = value
    document.dispatchEvent(new CustomEvent("territory:autosaving", { detail: { source: 'name' } }))

    fetch(this.form.action, {
      method: this.form.method.toUpperCase(),
      headers: {
        // LRPO-5797 — JSON, not turbo-stream. The turbo_stream failure branch used
        // to answer 200, so `response.ok` was true for a REJECTED save and this
        // controller reported "All changes saved" for a name that never persisted.
        // JSON gives an honest 422 plus the reason.
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      },
      body: new FormData(this.form)
    })
      .then(async response => {
        // ⚠ A 200 IS NOT A SAVE. find_territory REDIRECTS when the id is missing or
        // belongs to another exhibitor (measured: 302 to /exhibitor/territories,
        // text/html), and fetch FOLLOWS redirects — so `response.ok` is true and
        // the old check reported "All changes saved" for a territory this request never
        // touched. Reached by: territory deleted in another tab, event switched,
        // event no longer active. Requiring the server's own `status: "ok"` also
        // rejects any other 200 that is not this endpoint's success document; the
        // server was already sending it and this client was ignoring it.
        const payload = await response.json().catch(() => ({}))
        if (!response.ok || payload.status !== "ok") {
          // Carry the server's own wording through — "Not saved" with no reason
          // leaves the user guessing at what is a plain duplicate-name error.
          const reason = (payload.errors || []).join(", ")
          throw new Error(reason.length ? reason : "Territory name was not saved.")
        }
        this._setModalError(null)
        const header = document.getElementById("territory-name")
        if (header) header.textContent = value
        document.dispatchEvent(new CustomEvent("territory:autosaved", { detail: { source: 'name', name: value } }))
      })
      .catch(error => {
        console.error("Failed to auto-save territory name:", error)
        this.lastSaved = previous
        this._setModalError(error.message || "Territory name was not saved.")
        document.dispatchEvent(new CustomEvent("territory:autosave-failed", {
          detail: { source: 'name', message: error.message || "Territory name was not saved." }
        }))
      })
      .finally(() => {
        this.saving = false
        if (this.dirtyWhileSaving) {
          this.dirtyWhileSaving = false
          this.schedule()
        }
      })
  }

  // LRPO-5797 — put the reason where the user is actually looking.
  //
  // ★ THE BANNER IS NOT ENOUGH. This controller runs on the RENAME MODAL's form,
  // and the page-level feedback (#autosave-notice-slot, #autosave-status) sits
  // behind .modal-backdrop — z-index 1050 over the whole viewport, while .modal is
  // 1055 and neither feedback element sets one. With the dialog open, a correctly
  // refused save looked like nothing happening at all.
  //
  // ⚠ textContent, never innerHTML: this string is a server validation message.
  // ⚠ Passing null restores the slot's shipped state — hidden, holding the
  // client-side 250-character warning that the modal's own script toggles.
  _setModalError (message) {
    const slot = document.getElementById("territory-name-error")
    if (!slot) return

    if (message) {
      slot.textContent = message
      slot.classList.remove("d-none")
    } else {
      slot.textContent = "Territory name cannot exceed 250 characters."
      slot.classList.add("d-none")
    }
  }
}
